import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "../../../AppContext";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { FeatureFlagKey, PaymentTypes } from "../../../common/enums";
import {
  ICartItem,
  ICreateOrderPayload,
  ICustomerSearchResult,
  IInventoryProductSearchResult,
  IOrderViewModel,
} from "../models/cashdeskModels";
import { getDirectSalePaymentOptions } from "../../../common/utilities/paymentUtility";
import { PaymentTypesLabels } from "../../../common/labels";
import { IStudioSettingsModel } from "../../../common/models/IStudioSettingsModel";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { useFeatureFlag } from "../../../common/infrastructure/hooks/useFeatureFlag";
import { IGiftVoucherModel } from "../../administration/giftVouchers/models/giftVoucherModels";
import {
  canPaymentTypeGiveChange,
  evaluatePaymentAmount,
  fromCents,
  isPaymentMethodSelectable,
  toCents,
} from "../utils/cashdeskPaymentUtility";

interface IUseCashdeskSaleProps {
  studioSettings?: IStudioSettingsModel;
}

export const useCashdeskSale = ({ studioSettings }: IUseCashdeskSaleProps) => {
  const { selectedStudioId } = useAppContext();
  const { mutate } = useApiMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── Cart state ────────────────────────────────────────────────
  const [cart, setCart] = useState<ICartItem[]>([]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [notes, setNotes] = useState("");

  // ─── Customer state ────────────────────────────────────────────
  const [selectedCustomer, setSelectedCustomer] =
    useState<ICustomerSearchResult | null>(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");

  // ─── Product search state ──────────────────────────────────────
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);

  // ─── Payment state ─────────────────────────────────────────────
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] =
    useState<PaymentTypes | null>(null);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Gift-voucher tender state ─────────────────────────────────
  // `giftVoucherCode` is what the cashier is typing; `checkedVoucherCode` is what they
  // asked the server about. Splitting the two keeps the lookup from firing on every
  // keystroke, and keeps a stale balance from lingering under a half-edited code.
  const [giftVoucherCode, setGiftVoucherCode] = useState("");
  const [checkedVoucherCode, setCheckedVoucherCode] = useState("");

  // ─── Success state ─────────────────────────────────────────────
  const [successOrder, setSuccessOrder] = useState<IOrderViewModel | null>(
    null,
  );

  // ─── URL params ────────────────────────────────────────────────
  const [pendingUserId, setPendingUserId] = useState<string | null>(() => {
    return searchParams.get("userId");
  });

  // Clear userId from URL immediately.
  useEffect(() => {
    if (searchParams.has("userId")) {
      searchParams.delete("userId");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // ─── Customer list (loaded once on mount) ──────────────────────
  const { data: allCustomers } = useApiQuery<ICustomerSearchResult[]>({
    queryKey: [
      endpointsMetadata.cashdesk.searchCustomers,
      selectedStudioId,
    ],
    endpoint: `${endpointsMetadata.cashdesk.searchCustomers}?query=`,
  });

  const customerSearchResults = useMemo(() => {
    if (!allCustomers) return [];
    if (!customerSearchQuery.trim()) return allCustomers;
    const q = customerSearchQuery.toLowerCase();
    return allCustomers.filter(
      (c) =>
        c.firstName.toLowerCase().includes(q) ||
        c.lastName.toLowerCase().includes(q) ||
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
        (c.phoneNumber && c.phoneNumber.includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.rfidBandId && c.rfidBandId.toLowerCase().includes(q)) ||
        String(c.id) === q,
    );
  }, [allCustomers, customerSearchQuery]);

  // Once customers are fetched, auto-select from URL param then clear it.
  useEffect(() => {
    if (!pendingUserId || !allCustomers) return;
    const user = allCustomers.find((c) => c.id === Number(pendingUserId));
    if (user) {
      setSelectedCustomer(user);
      setCustomerSearchQuery("");
    }
    setPendingUserId(null);
  }, [allCustomers, pendingUserId]);


  // ─── Gift vouchers this studio can sell ────────────────────────
  // Only this studio's unsold, unexpired vouchers, and without their codes — a code is
  // what lets someone spend the balance, so it stays a manager-level secret.
  const { data: sellableVouchers } = useApiQuery<IGiftVoucherModel[]>({
    queryKey: [endpointsMetadata.giftVoucher.getSellable, selectedStudioId],
    endpoint: endpointsMetadata.giftVoucher.getSellable,
    globalIsLoading: false,
  });

  const isSellingVouchers = useMemo(
    () => cart.some((item) => !!item.giftVoucherId),
    [cart],
  );

  // Looks up the code the cashier asked about. The error toast is deliberately left ON:
  // the server's Bulgarian message already says exactly why the voucher will not work
  // (another studio's — 32005, expired — 32010, spent out — 32012).
  const { data: payingVoucher, isFetching: isLookingUpVoucher } =
    useApiQuery<IGiftVoucherModel>({
      queryKey: [endpointsMetadata.giftVoucher.lookupByCode, checkedVoucherCode],
      endpoint: `${endpointsMetadata.giftVoucher.lookupByCode}?code=${encodeURIComponent(
        checkedVoucherCode,
      )}`,
      enabled: checkedVoucherCode.length > 0,
      globalIsLoading: false,
      disableErrorToast: false,
      retry: false,
    });

  // ─── Payment options ───────────────────────────────────────────
  const isGiftVouchersEnabled = useFeatureFlag(FeatureFlagKey.EnableGiftVouchers, false);

  const paymentOptions = useMemo(() => {
    const options = getDirectSalePaymentOptions(studioSettings?.paymentTypes);

    // A voucher is bought with money, never with another voucher — the API rejects
    // that outright (32016), so the option is not even offered while the cart holds
    // vouchers being sold. It is never studio-configurable, so it is appended here
    // rather than read from the studio's tender list. Until gift vouchers are released
    // (the enable-gift-vouchers flag) the option is not offered at all.
    if (isSellingVouchers || !isGiftVouchersEnabled) {
      return options;
    }

    return [
      ...options,
      {
        id: PaymentTypes.GiftVoucher,
        name: PaymentTypesLabels[PaymentTypes.GiftVoucher],
      },
    ];
  }, [studioSettings, isSellingVouchers, isGiftVouchersEnabled]);

  // ─── Calculations ──────────────────────────────────────────────
  const subTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart],
  );

  // The discount applies to STOCK ONLY, exactly as the API computes it. A voucher's
  // face value is a fixed liability: discounting it would take less cash than the
  // studio then owes in services.
  const discountableSubTotal = useMemo(
    () =>
      cart
        .filter((item) => !item.giftVoucherId)
        .reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart],
  );

  const discountAmount = useMemo(
    () => discountableSubTotal * (discountPercent / 100),
    [discountableSubTotal, discountPercent],
  );

  const total = useMemo(
    () => Math.max(0, subTotal - discountAmount),
    [subTotal, discountAmount],
  );

  // ─── Payment amount (received / change) ────────────────────────
  const paymentAmountState = useMemo(
    () => evaluatePaymentAmount(toCents(total), toCents(receivedAmount)),
    [total, receivedAmount],
  );

  const changeAmount = fromCents(paymentAmountState.changeCents);

  const isPaymentMethodEnabled = useCallback(
    (type: PaymentTypes) => isPaymentMethodSelectable(type, paymentAmountState),
    [paymentAmountState],
  );

  // Drop an already-selected method once the entered amount makes it invalid
  // (e.g. the cashier overpays after having picked a card), or once it is no longer
  // offered (e.g. the enable-gift-vouchers flag turns off while the voucher is picked).
  useEffect(() => {
    if (
      selectedPaymentType !== null &&
      (!isPaymentMethodSelectable(selectedPaymentType, paymentAmountState) ||
        !paymentOptions.some((option) => option.id === selectedPaymentType))
    ) {
      setSelectedPaymentType(null);
    }
  }, [selectedPaymentType, paymentAmountState, paymentOptions]);

  // ─── Cart operations ──────────────────────────────────────────
  const addToCart = useCallback(
    (product: IInventoryProductSearchResult) => {
      setCart((prev) => {
        const existing = prev.find((item) => item.productId === product.id);
        if (existing) {
          return prev.map((item) =>
            item.productId === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item,
          );
        }
        return [
          ...prev,
          {
            productId: product.id,
            name: product.brand
              ? `${product.name} - ${product.brand}`
              : product.name,
            emoji: product.emoji || "📦",
            price: product.price,
            quantity: 1,
            currentStock: product.currentQuantity,
            lowStockThreshold: product.lowStockThreshold,
            unitOfMeasure: product.unitOfMeasure,
          },
        ];
      });
    },
    [],
  );

  /**
   * Puts a gift voucher in the cart at its face value. A voucher is a unique numbered
   * instrument, not a countable article, so the same one can never be added twice and
   * its quantity is locked at 1.
   */
  const addVoucherToCart = useCallback((voucher: IGiftVoucherModel) => {
    setCart((prev) => {
      if (prev.some((item) => item.giftVoucherId === voucher.id)) {
        return prev;
      }

      return [
        ...prev,
        {
          // Namespaced so a voucher id can never collide with a product id in the
          // cart's keying, which is what productId is used for everywhere else.
          productId: -voucher.id,
          giftVoucherId: voucher.id,
          name: `Подаръчен ваучер: ${voucher.name}`,
          emoji: "🎁",
          price: voucher.initialAmount,
          quantity: 1,
          // Vouchers are not stock. The large sentinel keeps every low-stock warning
          // and quantity guard in the cart from firing on a line that has no stock.
          currentStock: Number.MAX_SAFE_INTEGER,
          lowStockThreshold: 0,
          unitOfMeasure: 0,
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback(
    (productId: number, delta: number) => {
      setCart((prev) => {
        const updated = prev.map((item) =>
          // A voucher line's quantity is fixed at 1; the +/- buttons are hidden for it,
          // and this guard makes sure no other path can change it either.
          item.productId === productId && !item.giftVoucherId
            ? { ...item, quantity: item.quantity + delta }
            : item,
        );
        return updated.filter((item) => item.quantity > 0);
      });
    },
    [],
  );

  const setQuantity = useCallback(
    (productId: number, quantity: number) => {
      if (quantity <= 0) {
        setCart((prev) => prev.filter((item) => item.productId !== productId));
      } else {
        setCart((prev) =>
          prev.map((item) =>
            item.productId === productId && !item.giftVoucherId
              ? { ...item, quantity }
              : item,
          ),
        );
      }
    },
    [],
  );

  const removeFromCart = useCallback((productId: number) => {
    setCart((prev) => prev.filter((item) => item.productId !== productId));
  }, []);

  // ─── Beforeunload guard ────────────────────────────────────────
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    if (cart.length > 0) {
      window.addEventListener("beforeunload", handler);
    }
    return () => window.removeEventListener("beforeunload", handler);
  }, [cart.length]);

  // ─── Payment flow ──────────────────────────────────────────────
  const openPaymentModal = useCallback(() => {
    // Pre-fill the received amount with the exact total so the cashier only
    // has to edit it when the customer hands over more.
    setReceivedAmount(total);
    setSelectedPaymentType(null);
    setGiftVoucherCode("");
    setCheckedVoucherCode("");
    setIsPaymentModalOpen(true);
  }, [total]);

  const closePaymentModal = useCallback(() => {
    setIsPaymentModalOpen(false);
    setSelectedPaymentType(null);
    setGiftVoucherCode("");
    setCheckedVoucherCode("");
  }, []);

  /** Asks the server about the typed code. Editing the code drops the previous answer. */
  const checkVoucherCode = useCallback(() => {
    setCheckedVoucherCode(giftVoucherCode.trim().toUpperCase());
  }, [giftVoucherCode]);

  const changeVoucherCode = useCallback((code: string) => {
    setGiftVoucherCode(code.toUpperCase());
    setCheckedVoucherCode("");
  }, []);

  const isPayingWithVoucher = selectedPaymentType === PaymentTypes.GiftVoucher;

  /**
   * FULL-COVERAGE RULE, mirrored in the UI so the cashier is not told "no" only after
   * pressing confirm. The server enforces it again (32012) — this is convenience, not
   * the authority.
   */
  const isVoucherTenderReady =
    !!payingVoucher &&
    payingVoucher.code === checkedVoucherCode &&
    payingVoucher.remainingAmount + 1e-9 >= total;

  const confirmPayment = useCallback(() => {
    if (!selectedPaymentType || cart.length === 0) return;
    // Guard against an underpaid order slipping through (the UI also blocks this).
    if (paymentAmountState.isUnderpaid) return;
    // A voucher-paid order needs a code the server has already accepted.
    if (isPayingWithVoucher && !isVoucherTenderReady) return;

    setIsSubmitting(true);

    const payload: ICreateOrderPayload = {
      customerUserId: selectedCustomer?.id ?? null,
      discountPercent,
      paymentTypeId: selectedPaymentType,
      // Only cash-style methods carry a received amount (and thus change).
      receivedAmount: canPaymentTypeGiveChange(selectedPaymentType)
        ? Number(receivedAmount.toFixed(2))
        : null,
      notes: notes.trim() || null,
      items: cart
        .filter((item) => !item.giftVoucherId)
        .map((item) => ({
          inventoryItemId: item.productId,
          quantity: item.quantity,
        })),
      giftVoucherIds: cart
        .filter((item) => !!item.giftVoucherId)
        .map((item) => item.giftVoucherId!),
      giftVoucherCode: isPayingWithVoucher ? checkedVoucherCode : null,
      expectedTotal: Number(total.toFixed(2)),
    };

    mutate(
      {
        endpoint: endpointsMetadata.cashdesk.createOrder,
        method: "post",
        payload,
        disableSuccessToast: true,
        invalidateKeys: [
          [endpointsMetadata.cashdesk.getOrders],
          [endpointsMetadata.inventoryItemNomenclature.getAll],
          [endpointsMetadata.inventoryItemNomenclature.getAllCategorized],
          [endpointsMetadata.inventoryItemNomenclature.searchProducts],
          // A sold voucher leaves the sellable grid and a spent one changes balance,
          // so both voucher views have to be refetched after every order.
          [endpointsMetadata.giftVoucher.getSellable],
          [endpointsMetadata.giftVoucher.getAll],
        ],
      },
      {
        onSuccess: (data: IOrderViewModel) => {
          setSuccessOrder(data);
          setIsSubmitting(false);
        },
        onError: () => {
          setIsSubmitting(false);
        },
      },
    );
  }, [
    selectedPaymentType,
    cart,
    selectedCustomer,
    discountPercent,
    receivedAmount,
    paymentAmountState,
    notes,
    mutate,
    isPayingWithVoucher,
    isVoucherTenderReady,
    checkedVoucherCode,
    total,
  ]);

  const startNewOrder = useCallback(() => {
    setCart([]);
    setDiscountPercent(0);
    setNotes("");
    setSelectedCustomer(null);
    setCustomerSearchQuery("");
    setSelectedPaymentType(null);
    setReceivedAmount(0);
    setIsPaymentModalOpen(false);
    setSuccessOrder(null);
    setGiftVoucherCode("");
    setCheckedVoucherCode("");
  }, []);

  return {
    // Cart
    cart,
    addToCart,
    addVoucherToCart,
    updateQuantity,
    setQuantity,
    removeFromCart,
    subTotal,
    discountPercent,
    setDiscountPercent,
    discountAmount,
    total,
    notes,
    setNotes,

    // Customer
    selectedCustomer,
    setSelectedCustomer,
    customerSearchQuery,
    setCustomerSearchQuery,
    customerSearchResults: customerSearchResults ?? [],

    // Product search
    productSearchQuery,
    setProductSearchQuery,
    activeCategoryId,
    setActiveCategoryId,

    // Payment
    paymentOptions,
    isPaymentModalOpen,
    openPaymentModal,
    closePaymentModal,
    selectedPaymentType,
    setSelectedPaymentType,
    receivedAmount,
    setReceivedAmount,
    changeAmount,
    paymentAmountState,
    isPaymentMethodEnabled,
    isSubmitting,
    confirmPayment,

    // Gift vouchers
    sellableVouchers: sellableVouchers ?? [],
    isSellingVouchers,
    giftVoucherCode,
    changeVoucherCode,
    checkVoucherCode,
    payingVoucher: payingVoucher?.code === checkedVoucherCode ? payingVoucher : undefined,
    isLookingUpVoucher,
    isPayingWithVoucher,
    isVoucherTenderReady,

    // Success
    successOrder,
    startNewOrder,
  };
};
